import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CustombaseComponent } from 'src/app/custombase/custombase.component';

declare const window: any;

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * CHATBOT ACCESS KEY
 * ─────────────────────────────────────────────────────────────────────────────
 * window.__env.chatbotEnabled must match this EXACT string for the AI
 * Assistant FAB to appear. true / false / any other value = hidden.
 *
 * Share this key ONLY with authorized DevOps personnel.
 * To rotate: change the string here, rebuild & redeploy.
 *
 * How it is used in env.js:
 *   window.__env.chatbotEnabled = 'XEDGE-AI-7F2K-VIAVI-2k26-PROD';  ← enable
 *   window.__env.chatbotEnabled = false;                              ← disable
 * ─────────────────────────────────────────────────────────────────────────────
 */
const CHATBOT_ACCESS_KEY = 'AIassis-_7F2KviAVI2k26PROD274';

/**
 * ChatConfigService
 * ─────────────────
 * Single source-of-truth for every runtime configuration value.
 * All values are read from window.__env which is loaded by env.js
 * BEFORE the Angular bundles — so no rebuild is needed to reconfigure.
 */
@Injectable()
export class ChatConfigService extends CustombaseComponent {

  /** * Stores the current chatbot visibility. 
   * * * BehaviorSubject is used so that: * - ChatbotComponent gets the current value immediately. 
   * * - Role changes can trigger a new value. * - Angular can react to the change through the async pipe. 
   * */
  private readonly chatbotEnabledSubject = 
    new BehaviorSubject<boolean>(
      this.calculateChatbotEnabled()
    ); 
  /** * Public observable used by ChatbotComponent. */
  readonly chatbotEnabled$: Observable<boolean> = this.chatbotEnabledSubject.asObservable();
  private controllerResolution$: Promise<string> | null = null;
  private controllerConfirmed = false;
  // ── Backend URL ────────────────────────────────────────────────────────────

  get backendUrl(): string {
    const raw: string = window?.__env?.chatbotUrl;

    // Mixed-content guard:
    // Auto-upgrade http:// → https:// when the page is served over HTTPS
    // so browsers never block the API calls as mixed content.
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      raw?.startsWith('http:')
    ) {
      return raw.replace(/^http:\/\//, 'https://');
    }

    return raw;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  get kuzzleToken(): string {
    return localStorage.getItem('token') || '';
  }

  /**
   * Optional MCP HTTP adapter token.
   * Required only when MCP_HTTP_TOKEN is set on the adapter.
   * Stored in localStorage under 'mcp_token' — leave blank if not used.
   */
  get mcpToken(): string {
    return localStorage.getItem('mcp_token') || '';
  }

  // ── Controller ────────────────────────────────────────────────────────────

  get controllerId(): string {
    return localStorage.getItem('chat_controller_id') || 'dev';
  }

  set controllerId(val: string) {
    localStorage.setItem('chat_controller_id', val);
  }

  // ── Feature flag ──────────────────────────────────────────────────────────

 /** * ⭐ INTERNAL CALCULATION * 
  * * This is the actual logic that determines whether the chatbot * should be available.
  * * Environment flag + role permission MUST both pass. */ 
  private calculateChatbotEnabled(): boolean {
    // Always load the latest role permissions
    this.refreshRoleRights(); 
    const flag = window?.__env?.chatbotEnabled; 
    const isChatbotVisible = this.chatbotRight(); 
    return ( 
      typeof flag === 'string' && 
      flag.trim() === CHATBOT_ACCESS_KEY && 
      isChatbotVisible 
    ); 
  } 
  /** * ⭐ REFRESH CHATBOT VISIBILITY
   * * Call this whenever the user's role/permissions may have changed. 
   * * * If the calculated value is the same, no unnecessary UI update * is generated. 
   * */ 
  refreshChatbotVisibility(): void { 
    const enabled = this.calculateChatbotEnabled(); 
    if (enabled !== this.chatbotEnabledSubject.value) { 
      this.chatbotEnabledSubject.next(enabled); 
    } 
  } 
  /** * ⭐ CURRENT SYNCHRONOUS VALUE 
   * * Useful if some non-template code needs to know the current state. 
   * */ 
  get isChatbotEnabled(): boolean { 
    return this.chatbotEnabledSubject.value; 
  }

  get isConfigured(): boolean {
    return !!this.backendUrl && !!this.kuzzleToken;
  }

  // ── HTTP header builders ──────────────────────────────────────────────────

  buildHeaders(): Record<string, string> {
    const headerKey = window?.__env?.chatbotHeader || 'X-Kuzzle-Token';
    const h: Record<string, string> = {
      'Content-Type':    'application/json',
      [headerKey]:  this.kuzzleToken,
      'X-Controller-Id': this.controllerId,
    };
    const mcp = this.mcpToken;
    if (mcp) h['X-MCP-Token'] = mcp;
    return h;
  }

  buildGetHeaders(): Record<string, string> {
    const headerKey = window?.__env?.chatbotHeader || 'X-Kuzzle-Token';
    const h: Record<string, string> = {
      [headerKey]:  this.kuzzleToken,
      'X-Controller-Id': this.controllerId,
    };
    const mcp = this.mcpToken;
    if (mcp) h['X-MCP-Token'] = mcp;
    return h;
  }

  /** True only once resolveControllerId() found a real match against the backend. */
  get isControllerConfirmed(): boolean {
    return this.controllerConfirmed;
  }
  /** Any caller needing controllerId MUST await this first */
  ensureControllerId(): Promise<string> {
    if (this.controllerConfirmed && this.controllerResolution$) {
      return this.controllerResolution$;
    }
    this.controllerResolution$ = this.resolveControllerId();
    return this.controllerResolution$;
  }

  /** Call this explicitly on login AND logout */
  resetController(): void {
    this.controllerResolution$ = null;
    this.controllerConfirmed = false;
    localStorage.removeItem('chat_controller_id');
  }

  // ── Controller auto-resolution ────────────────────────────────────────────

  async resolveControllerId(): Promise<string> {
    try {
      const res = await fetch(`${this.backendUrl}/api/controllers`);
      if (!res.ok) throw new Error('controllers fetch failed');

      const data: { controllers: { id: string; name: string; [key: string]: string }[] }
        = await res.json();

      const hostField = window?.__env?.chatbotControllerHost || 'xedge_host';
      const currentHost = window.location.hostname.toLowerCase();

      const getHost = (c: { [key: string]: string }): string =>
      (c[hostField] ?? c['xedge_host'] ?? c['kuzzle_host'] ?? '').toLowerCase();

      const matched = data.controllers.find(c =>
        getHost(c) === currentHost
      );
      if (matched) {
        this.controllerId = matched.id;
        this.controllerConfirmed = true;
        console.log(`[Chatbot] Controller resolved: ${matched.id} (field: ${hostField})`);
        return matched.id;
      }

      const partial = data.controllers.find(c => {
        const host = getHost(c);
        if (!host && !c.id) return false;         // ← fixes the '' .includes('') landmine
        return (
          currentHost.includes(c.id.toLowerCase()) ||
          currentHost.includes(host.split('.')[0])
        );
      });
      if (partial) {
        this.controllerId = partial.id;
        this.controllerConfirmed = true;
        console.log(`[Chatbot] Controller resolved (partial): ${partial.id} (field: ${hostField})`);
        return partial.id;
      }

      this.controllerConfirmed = false;
      console.warn(`[Chatbot] No controller matched "${currentHost}", using cached: ${this.controllerId}`);
      return this.controllerId;

    } catch (err) {
      this.controllerConfirmed = false;
      console.warn('[Chatbot] Could not resolve controller, using cached:', this.controllerId, err);
      return this.controllerId;
    }
  }
}